// «Nuova attività» a pagina intera (tela, artboard 41 e 45): sei passi nell'ordine tipo → chi e
// dove → compito → quando → notifica → riepilogo, con l'anteprima di come apparirà in Pianifica. Serve
// anche a modificarne una: parte dai suoi valori.
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type {
  AgentProfile,
  ConnectedProvider,
  MemoWatch,
  Pipeline,
  Project,
  Routine,
  ToolGroup,
} from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { grants, modelLabel } from '../agents/AgentEditor';
import { HookReveal } from './RoutineExtras';
import { DELIVERY, KINDS, StepKind, StepTask, StepWhen, StepWho } from './RoutineSteps';
import { StepNotify } from './StepNotify';
import { STEPS, bodyOf, fromRoutine, scheduleOf, validity, workerOf, type Draft } from './draft';
import { describeCondition, describeWhen } from './when';

export function RoutinePage({
  pipelines,
  editing,
  onClose,
  onSaved,
  onRun,
}: {
  pipelines: Pipeline[];
  editing?: Routine;
  onClose: () => void;
  onSaved: () => void;
  onRun: (r: Routine) => void;
}) {
  const [d, setDraft] = useState<Draft>(() => fromRoutine(editing, pipelines));
  const set = (patch: Partial<Draft>) => setDraft((x) => ({ ...x, ...patch }));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hook, setHook] = useState<{ token: string; path: string } | null>(null);
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [watched, setWatched] = useState<MemoWatch[]>([]);
  const [groups, setGroups] = useState<ToolGroup[]>([]);
  const [providers, setProviders] = useState<ConnectedProvider[]>([]);

  useEffect(() => {
    void api.profiles().then(setProfiles, () => setProfiles([]));
    void api.projects().then(
      (r) => setProjects(r.projects),
      () => setProjects([]),
    );
    void api.integrations().then(
      (r) => setWatched(r.watched),
      () => setWatched([]),
    );
    void api.tools().then(
      (c) => setGroups(c.groups),
      () => setGroups([]),
    );
    void api.providers().then(
      (p) => setProviders(p.providers),
      () => setProviders([]),
    );
  }, []);

  // ogni passo si apre dall'alto della pagina
  useEffect(() => {
    document.querySelector('.page')?.scrollIntoView({ block: 'start' });
  }, [step]);

  const valid = validity(d);
  const reachable = (i: number) => valid.slice(0, i).every(Boolean);
  const last = step === STEPS.length - 1;
  const operational = d.options.kind === 'operational';
  const worker = workerOf(d, profiles);
  const model = d.options.model
    ? modelLabel(d.options.model) + (d.options.effort ? ` · ragionamento ${d.options.effort}` : '')
    : `Quello del pop · ${worker?.effectiveModel ? modelLabel(worker.effectiveModel) : '—'}`;
  const when =
    d.trigger === 'condition'
      ? describeCondition(d.condition, groups)
      : describeWhen(d.trigger, scheduleOf(d), d.trigger === 'input' ? d.inputHint : null);
  const project = projects.find((p) => p.id === d.project)?.name ?? 'Nessun progetto';
  const toolNames =
    grants(groups)
      .filter((g) => d.options.tools.includes(g.value))
      .map((g) => g.label)
      .join(', ') || 'nessuno';
  const kind = KINDS.find((k) => k.id === d.options.kind) ?? KINDS[0];
  const channels =
    (d.options.notify ?? [])
      .map((n) => {
        const g = groups.find((x) => x.id === n.connection);
        return `${g?.name ?? n.connection} → ${n.recipients.join(', ')}`;
      })
      .join('; ') || 'nessuna: il risultato resta nella sessione';
  // i canali nell'anteprima: «Email a 2», «Push a luca»
  const notifyTags = (d.options.notify ?? [])
    .filter((n) => n.recipients.length)
    .map((n) => {
      const push = groups.find((x) => x.id === n.connection)?.kind === 'pushover';
      const who = n.recipients.length === 1 ? n.recipients[0] : String(n.recipients.length);
      return `${push ? 'Push' : 'Email'} a ${who}`;
    });

  const save = async (andRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const body = bodyOf(d, editing?.enabled ?? true);
      const saved = editing
        ? await api.updateRoutine(editing.id, body)
        : await api.createRoutine(body);
      onSaved();
      // un hook nuovo ha bisogno del suo token: si mostra adesso, una volta sola
      if (d.trigger === 'hook' && !(editing?.hook ?? false)) setHook(await api.hookToken(saved.id));
      else if (andRun) onRun(saved);
      else onClose();
    } catch (e) {
      setError(e instanceof ApiError && e.detail ? e.detail : 'Non riesco a salvare.');
    } finally {
      setBusy(false);
    }
  };

  const rows: [string, string][] = [
    ['Tipo', kind.title],
    ...(pipelines.length > 1
      ? [
          ['Filiera', pipelines.find((p) => p.id === d.pipeline)?.name ?? d.pipeline] as [
            string,
            string,
          ],
        ]
      : []),
    ['Progetto', project],
    ['Chi lavora', worker?.label ?? '—'],
    ['Modello', model],
    ['Nome', d.name.trim() || '—'],
    ['Cosa deve fare', d.task.trim() || '—'],
    ['Strumenti', toolNames],
    ['Risultato', DELIVERY.find((x) => x.id === (d.options.deliver ?? 'session'))?.title ?? ''],
    ['Quando', when],
    ['Notifica', channels],
  ];

  return (
    <div className="page rp-page">
      <div>
        <button type="button" className="rp-back" onClick={onClose}>
          <Icon name="arrow-left" size={14} /> Pianifica
        </button>
        <h1 className="t-h1" style={{ margin: '6px 0 4px' }}>
          {editing ? 'Modifica attività' : 'Nuova attività'}
        </h1>
        <p className="t-body dim" style={{ margin: 0 }}>
          Un lavoro che PopTown sa ripetere: a mano, a orario o quando succede qualcosa.
        </p>
      </div>
      {hook ? (
        <section className="rp-card">
          <HookReveal token={hook.token} path={hook.path} onDone={onClose} />
        </section>
      ) : (
        <>
          <ol aria-label="Passi" className="rp-steps">
            {STEPS.map((label, i) => (
              <li key={label}>
                <button
                  type="button"
                  className="rp-step"
                  aria-current={i === step ? 'step' : undefined}
                  data-done={i < step || undefined}
                  disabled={!reachable(i)}
                  onClick={() => setStep(i)}
                >
                  <span className="rp-step-dot">{i < step ? '✓' : i + 1}</span>
                  <span className="rp-step-label">{label}</span>
                </button>
              </li>
            ))}
          </ol>
          <div className="rp-body">
            <div className="rp-main">
              <section className="rp-card" aria-label={STEPS[step]}>
                <h2 className="t-h3" style={{ margin: 0 }}>
                  {STEPS[step]}
                </h2>
                {step === 0 && <StepKind d={d} set={set} pipelines={pipelines} />}
                {step === 1 && (
                  <StepWho
                    d={d}
                    set={set}
                    profiles={profiles}
                    projects={projects}
                    providers={providers}
                  />
                )}
                {step === 2 && <StepTask d={d} set={set} groups={groups} />}
                {step === 3 && <StepWhen d={d} set={set} watched={watched} groups={groups} />}
                {step === 4 && (
                  <StepNotify
                    value={d.options.notify ?? []}
                    onChange={(notify) => set({ options: { ...d.options, notify } })}
                    groups={groups}
                  />
                )}
                {step === 5 && (
                  <>
                    <dl className="rp-summary">
                      {rows.map(([k, v]) => (
                        <div key={k} style={{ display: 'contents' }}>
                          <dt className="t-small dim">{k}</dt>
                          <dd className="t-body">{v}</dd>
                        </div>
                      ))}
                    </dl>
                    {!operational || d.project ? null : (
                      <Banner kind="warn">Manca il progetto: torna al passo 2.</Banner>
                    )}
                    {d.trigger !== 'input' && d.trigger !== 'hook' && (
                      <Banner kind="info">
                        «Salva e avvia» la salva e la fa partire subito: la sessione si apre e la
                        segui dal vivo.
                      </Banner>
                    )}
                  </>
                )}
              </section>
              {error && <Banner kind="danger">{error}</Banner>}
              <div className="rp-footer">
                <Button variant="secondary" disabled={step === 0} onClick={() => setStep(step - 1)}>
                  Indietro
                </Button>
                <span style={{ flex: 1 }} />
                <Button variant="ghost" onClick={onClose}>
                  Annulla
                </Button>
                {last ? (
                  <>
                    <Button
                      variant={
                        d.trigger === 'input' || d.trigger === 'hook' ? 'primary' : 'secondary'
                      }
                      loading={busy}
                      disabled={!valid.every(Boolean)}
                      onClick={() => void save(false)}
                    >
                      Salva
                    </Button>
                    {d.trigger !== 'input' && d.trigger !== 'hook' && (
                      <Button
                        variant="primary"
                        loading={busy}
                        disabled={!valid.every(Boolean)}
                        onClick={() => void save(true)}
                      >
                        Salva e avvia
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="primary"
                    disabled={!valid[step]}
                    onClick={() => setStep(step + 1)}
                  >
                    Avanti
                  </Button>
                )}
              </div>
            </div>
            <aside aria-label="Anteprima" className="rp-aside">
              <div className="t-overline faint">Come apparirà in Pianifica</div>
              <div className="rp-card rp-preview">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span className="rp-preview-icon">
                    <Icon name={kind.icon} size={18} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="t-strong">{d.name.trim() || 'Senza nome'}</div>
                    <div className="t-caption dim">
                      {kind.title}
                      {worker ? ` · ${worker.label}` : ''}
                    </div>
                  </div>
                </div>
                <div className="t-small dim rp-preview-task">
                  {d.task.trim() || 'Scrivi cosa deve fare al passo 3.'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[when, project, model, ...notifyTags].map((x) => (
                    <span key={x} className="rp-tag">
                      {x}
                    </span>
                  ))}
                </div>
              </div>
              <div className="t-caption faint">
                Si aggiorna mentre compili. Puoi tornare a un passo già fatto cliccandolo.
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
